import { supabase } from './supabaseClient';
import { kpiAdminService } from './kpiAdminService';

export const piAdminService = {
  // PI Systems
  async getPISystems() {
    const { data, error } = await supabase
      .from('pi_systems')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async getPISystem(piSystemId) {
    const { data, error } = await supabase
      .from('pi_systems')
      .select('*')
      .eq('id', piSystemId)
      .single();
    if (error) throw error;
    return data;
  },

  async createPISystem(systemData) {
    const { data, error } = await supabase
      .from('pi_systems')
      .insert([systemData])
      .select();
    if (error) throw error;
    return data[0];
  },

  async updatePISystem(piSystemId, updates) {
    const { data, error } = await supabase
      .from('pi_systems')
      .update(updates)
      .eq('id', piSystemId)
      .select();
    if (error) throw error;
    return data[0];
  },

  async deactivatePISystem(piSystemId) {
    return this.updatePISystem(piSystemId, { is_active: false });
  },

  // Catalog (fixed, seeded reference data)
  async getPIParameters() {
    const { data, error } = await supabase
      .from('pi_parameters')
      .select('*')
      .order('sort_order', { ascending: true });
    if (error) throw error;
    return data;
  },

  // Per-system enabled params (join to the catalog)
  async getSystemParameters(piSystemId) {
    const { data, error } = await supabase
      .from('pi_system_parameters')
      .select('*, pi_parameters(*)')
      .eq('pi_system_id', piSystemId)
      .order('sort_order', { ascending: true });
    if (error) throw error;
    return data;
  },

  // Replace the system's enabled catalog params with the given set.
  // Delete-then-insert keeps it simple and matches the "checkbox list, Save"
  // UX; sort_order follows the order parameterIds is given in.
  async setSystemParameters(piSystemId, parameterIds) {
    const { error: deleteError } = await supabase
      .from('pi_system_parameters')
      .delete()
      .eq('pi_system_id', piSystemId);
    if (deleteError) throw deleteError;

    if (!parameterIds || parameterIds.length === 0) return;

    const rows = parameterIds.map((pi_parameter_id, index) => ({
      pi_system_id: piSystemId,
      pi_parameter_id,
      sort_order: index,
    }));
    const { error: insertError } = await supabase
      .from('pi_system_parameters')
      .insert(rows);
    if (insertError) throw insertError;
  },

  // ---- Reward formulas (one active formula per PI System) ----
  // Returns the system's reward formula plus its gates/rates/tiers, or a null
  // formula with empty child arrays when none is configured. Mirrors the
  // getSystemParameters read shape (read the parent, then the children).
  async getRewardFormula(piSystemId) {
    const { data: formula, error } = await supabase
      .from('pi_reward_formulas')
      .select('*')
      .eq('pi_system_id', piSystemId)
      .maybeSingle();
    if (error) throw error;
    if (!formula) return { formula: null, gates: [], rates: [], tiers: [] };

    const [gatesRes, ratesRes, tiersRes] = await Promise.all([
      supabase.from('pi_reward_gates').select('*').eq('formula_id', formula.id).order('sort_order', { ascending: true }),
      supabase.from('pi_reward_rates').select('*').eq('formula_id', formula.id).order('metric', { ascending: true }),
      supabase.from('pi_reward_tiers').select('*').eq('formula_id', formula.id).order('sort_order', { ascending: true }),
    ]);
    if (gatesRes.error) throw gatesRes.error;
    if (ratesRes.error) throw ratesRes.error;
    if (tiersRes.error) throw tiersRes.error;

    return {
      formula,
      gates: gatesRes.data || [],
      rates: ratesRes.data || [],
      tiers: tiersRes.data || [],
    };
  },

  // Atomic-ish replace of a system's reward formula. Upsert the formula row by
  // its unique pi_system_id, then delete + re-insert its gates/rates/tiers with
  // sort_order following array index. Mirrors setSystemParameters' delete-then-
  // insert; the upsert keeps a stable formula id so existing snapshots' FKs and
  // the unique(pi_system_id) constraint both hold. Returns the formula row.
  async saveRewardFormula(piSystemId, payload) {
    const { payout_type, base_amount, cap_amount, tier_metric, gates, rates, tiers } = payload;

    const { data, error } = await supabase
      .from('pi_reward_formulas')
      .upsert(
        {
          pi_system_id: piSystemId,
          payout_type,
          base_amount,
          cap_amount: cap_amount ?? null,
          tier_metric: tier_metric ?? null,
          is_active: true,
        },
        { onConflict: 'pi_system_id' }
      )
      .select();
    if (error) throw error;
    const formula = data[0];

    const replaceChildren = async (table, rows) => {
      const { error: delError } = await supabase.from(table).delete().eq('formula_id', formula.id);
      if (delError) throw delError;
      if (!rows || rows.length === 0) return;
      const { error: insError } = await supabase.from(table).insert(rows);
      if (insError) throw insError;
    };

    await replaceChildren(
      'pi_reward_gates',
      (gates || []).map((g, index) => ({
        formula_id: formula.id,
        metric: g.metric,
        op: g.op,
        threshold: g.threshold,
        sort_order: index,
      }))
    );
    await replaceChildren(
      'pi_reward_rates',
      (rates || []).map((r) => ({
        formula_id: formula.id,
        metric: r.metric,
        rate: r.rate,
      }))
    );
    await replaceChildren(
      'pi_reward_tiers',
      (tiers || []).map((t, index) => ({
        formula_id: formula.id,
        min_value: t.min_value,
        max_value: t.max_value ?? null,
        amount: t.amount,
        sort_order: index,
      }))
    );

    return formula;
  },

  // User PI System Assignments
  async getUserPIAssignments() {
    const { data, error } = await supabase
      .from('user_pi_system_assignments')
      .select('*, user_id, pi_system_id')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async assignPISystemToUser(userId, piSystemId) {
    const { data, error } = await supabase
      .from('user_pi_system_assignments')
      .insert([{
        user_id: userId,
        pi_system_id: piSystemId,
        is_active: true,
      }])
      .select();
    if (error) throw error;
    return data[0];
  },

  async removePISystemFromUser(userId, piSystemId) {
    const { error } = await supabase
      .from('user_pi_system_assignments')
      .delete()
      .eq('user_id', userId)
      .eq('pi_system_id', piSystemId);
    if (error) throw error;
  },

  async deactivateUserPIAssignment(assignmentId) {
    const { data, error } = await supabase
      .from('user_pi_system_assignments')
      .update({ is_active: false })
      .eq('id', assignmentId)
      .select();
    if (error) throw error;
    return data[0];
  },

  // Reuse the existing users query so PI assignment UI has one source of truth.
  async getAllUsers() {
    return kpiAdminService.getAllUsers();
  },
};
